import * as React from 'react'
import { createSelectItemEvent } from '@/scopes/checkout/domain/analytics/index'
import { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import { useSelectedServicesStorage } from '@/scopes/checkout/domain/analytics/useSelectedServicesStorage'
import { type BookingDraft, type ServiceMappers, type Services, ServiceType } from '@/scopes/checkout/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

interface Dependencies {
  serviceMappers: ServiceMappers
}
export function useSendSelectItem(dependencies: Partial<Dependencies>) {
  const { sendEvent } = useAnalytics()
  const { addItem, removeItem, trackRemovedItem, hasSubmittedToStep2 } = useSelectedServicesStorage()
  const { serviceMappers } = dependencies as Dependencies

  const sendSelectItemEvent = React.useCallback(
    <K extends ServiceType>(service: Services[K], serviceType: K, previousBookingDraft: BookingDraft) => {
      if (!service)
        return

      const serviceMapper = serviceMappers[serviceType]
      const checkoutProps = previousBookingDraft.analytics
      const step = checkoutProps?.step ?? 1

      const newItems = serviceMapper.mapSelectedItemToGA4(service, step)

      const previousService = previousBookingDraft.services[serviceType]
      const prevItems = previousService
        ? serviceMapper.mapSelectedItemToGA4(previousService as Services[K], step)
        : []

      if (newItems.length) {
        emitNetNewItems(newItems, prevItems, (item, index) => {
          sendEvent(createSelectItemEvent([{ ...item, index }], checkoutProps))
        })
      }

      syncSelectedItemsStorage(newItems, prevItems, addItem, removeItem, trackRemovedItem, hasSubmittedToStep2)
    },
    [serviceMappers, sendEvent, addItem, removeItem, trackRemovedItem, hasSubmittedToStep2],
  )

  const sendSelectItemGenericServiceEvent = React.useCallback(
    <K extends ServiceType>(service: Services[K], serviceType: K, previousBookingDraft: BookingDraft) => {
      if (!service)
        return

      const isGenericService
        = serviceType === ServiceType.GenericServicePerBooking
          || serviceType === ServiceType.GenericServicePerParticipant
          || serviceType === ServiceType.GenericServicePerRoom

      const previousService = isGenericService
        ? (previousBookingDraft.services[serviceType] as unknown as Record<string, Services[K]>)[service.id]
        : previousBookingDraft.services[serviceType]

      const serviceMapper = serviceMappers[serviceType]
      const checkoutProps = previousBookingDraft.analytics
      const step = checkoutProps?.step ?? 1

      const newItems = serviceMapper.mapSelectedItemToGA4(service, step)

      const prevItems = previousService
        ? serviceMapper.mapSelectedItemToGA4(previousService as Services[K], step)
        : []

      if (newItems.length) {
        emitNetNewItems(newItems, prevItems, (item, index) => {
          sendEvent(createSelectItemEvent([{ ...item, index }], checkoutProps))
        })
      }

      syncSelectedItemsStorage(newItems, prevItems, addItem, removeItem, trackRemovedItem, hasSubmittedToStep2)
    },
    [serviceMappers, sendEvent, addItem, removeItem, trackRemovedItem, hasSubmittedToStep2],
  )

  function emitNetNewItems(
    newItems: ImpressionViewAncillariesItem[],
    prevItems: ImpressionViewAncillariesItem[],
    emit: (item: ImpressionViewAncillariesItem, index: number) => void,
  ) {
    const prevCounts = prevItems.reduce<Record<string, number>>((acc, item) => {
      acc[item.item_id] = (acc[item.item_id] ?? 0) + 1
      return acc
    }, {})
    const newCounts = newItems.reduce<Record<string, number>>((acc, item) => {
      acc[item.item_id] = (acc[item.item_id] ?? 0) + 1
      return acc
    }, {})

    let eventIndex = 1
    const processed = new Set<string>()
    newItems.forEach((item) => {
      if (processed.has(item.item_id))
        return
      processed.add(item.item_id)

      const prevCount = prevCounts[item.item_id] ?? 0
      const newCount = newCounts[item.item_id] ?? 0
      for (let i = prevCount; i < newCount; i++) {
        emit(item, eventIndex++)
      }
    })
  }

  function syncSelectedItemsStorage(
    newItems: ImpressionViewAncillariesItem[],
    prevItems: ImpressionViewAncillariesItem[],
    upsertItem: (item: ImpressionViewAncillariesItem) => void,
    removeStoredItem: (itemId: string) => void,
    trackRemoved: (item: ImpressionViewAncillariesItem) => void,
    shouldTrackRemoved: () => boolean,
  ) {
    const newItemIds = new Set(newItems.map(item => item.item_id))

    newItems.forEach((item) => {
      upsertItem(item)
    })

    prevItems.forEach((item) => {
      if (!newItemIds.has(item.item_id)) {
        removeStoredItem(item.item_id)

        if (shouldTrackRemoved())
          trackRemoved(item)
      }
    })
  }

  return {
    sendSelectItemEvent,
    sendSelectItemGenericServiceEvent,
  }
}
